import { useState, useEffect, useCallback, useRef } from 'react';
import PointsService from '../../services/pointsService';
import { logger } from '../../utils/logger';

interface PointEvent {
  id: string;
  eventType: string;
  points: number;
  description?: string;
  appliesTo: 'member' | 'family';
  createdAt: string;
}

interface PointsHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  memberName: string;
}

type FilterType = 'all' | 'positive' | 'negative' | 'streak';
type FilterDate = 'all' | 'today' | 'week' | 'month';

export default function PointsHistoryModal({ isOpen, onClose, memberId, memberName }: PointsHistoryModalProps) {
  const [events, setEvents] = useState<PointEvent[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<PointEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [filterDate, setFilterDate] = useState<FilterDate>('all');
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [memberTotalPoints, setMemberTotalPoints] = useState<number | null>(null);
  const pointsServiceRef = useRef(PointsService.getInstance());
  const offsetRef = useRef(0);
  const LIMIT = 20;

  const loadPointsHistory = useCallback(async (reset: boolean = false) => {
    logger.sensitive('Carregando histórico de pontos para', { memberId });
    
    if (reset) {
      setLoading(true);
      setEvents([]);
      setMemberTotalPoints(null);
      offsetRef.current = 0;
    } else {
      setLoadingMore(true);
    }

    try {
      const currentOffset = offsetRef.current;
      const pointsService = pointsServiceRef.current;
      const history = await pointsService.getMemberPointHistory(memberId, LIMIT, currentOffset);
      const count = await pointsService.getMemberPointHistoryCount(memberId);
      
      console.log('✅ [PointsHistory] Histórico carregado:', history.length, 'eventos de', count, 'totais');
      
      if (reset) {
        setEvents(history);
        const pointsSnapshot = await pointsService.getMemberPoints(memberId);
        setMemberTotalPoints(pointsSnapshot?.totalPoints ?? null);
      } else {
        setEvents(prev => [...prev, ...history]);
      }
      
      setTotalCount(count);
      const newOffset = currentOffset + history.length;
      offsetRef.current = newOffset;
      setHasMore(newOffset < count);
    } catch (error) {
      console.error('❌ [PointsHistory] Erro ao carregar histórico:', error);
      if (reset) {
        setEvents([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [memberId]);

  useEffect(() => {
    if (isOpen && memberId) {
      void loadPointsHistory(true);
    }
  }, [isOpen, memberId, loadPointsHistory]);

  // Recarregar automaticamente quando o dashboard sinalizar atualização de pontos
  useEffect(() => {
    const handler = (ev: Event) => {
      const anyEv = ev as CustomEvent<{ memberId?: string }>;
      if (!isOpen) return;
      if (anyEv.detail?.memberId && anyEv.detail.memberId !== memberId) return;
      void loadPointsHistory(true);
    };
    window.addEventListener('magistral:points-updated', handler as EventListener);
    return () => window.removeEventListener('magistral:points-updated', handler as EventListener);
  }, [isOpen, memberId, loadPointsHistory]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      void loadPointsHistory(false);
    }
  }, [hasMore, loadPointsHistory, loadingMore]);

  const applyFilters = useCallback(() => {
    let filtered = [...events];

    // Filtro por tipo
    if (filterType === 'positive') {
      filtered = filtered.filter(e => e.points > 0);
    } else if (filterType === 'negative') {
      filtered = filtered.filter(e => e.points < 0);
    } else if (filterType === 'streak') {
      filtered = filtered.filter(e => e.eventType.toLowerCase().includes('streak') || e.eventType.toLowerCase().includes('dias'));
    }

    // Filtro por data
    if (filterDate !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(e => {
        const eventDate = new Date(e.createdAt);
        
        if (filterDate === 'today') {
          return eventDate >= today;
        } else if (filterDate === 'week') {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return eventDate >= weekAgo;
        } else if (filterDate === 'month') {
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return eventDate >= monthAgo;
        }
        
        return true;
      });
    }

    setFilteredEvents(filtered);
  }, [events, filterDate, filterType]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date >= today) {
      return `Hoje às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date >= yesterday) {
      return `Ontem às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    }
  };

  const isLevelEvent = (eventType: string) => {
    return eventType.toLowerCase().includes('nível atualizado');
  };

  const isNeutralEvent = (points: number, eventType: string) => {
    // Eventos neutros: pontos zero mas não são eventos de nível
    return points === 0 && !isLevelEvent(eventType);
  };

  const getEventIcon = (points: number, eventType: string) => {
    if (eventType.toLowerCase().includes('streak') || eventType.toLowerCase().includes('dias')) {
      return 'ri-fire-fill';
    } else if (isLevelEvent(eventType)) {
      return 'ri-arrow-up-circle-fill'; // Seta para cima (azul)
    } else if (isNeutralEvent(points, eventType)) {
      return 'ri-arrow-right-circle-fill'; // Seta para direita (cinza)
    } else if (points > 0) {
      return 'ri-arrow-up-circle-fill';
    } else {
      return 'ri-arrow-down-circle-fill';
    }
  };

  const getEventColor = (points: number, eventType: string) => {
    if (isLevelEvent(eventType)) {
      return 'text-blue-500'; // Azul para eventos de nível
    } else if (isNeutralEvent(points, eventType)) {
      return 'text-gray-500'; // Cinza para eventos neutros
    } else if (points > 0) {
      return 'text-green-500';
    } else {
      return 'text-red-500';
    }
  };

  const getEventBgColor = (points: number, eventType: string) => {
    if (isLevelEvent(eventType)) {
      return 'bg-blue-50'; // Fundo azul claro para eventos de nível
    } else if (isNeutralEvent(points, eventType)) {
      return 'bg-gray-50'; // Fundo cinza claro para eventos neutros
    } else if (points > 0) {
      return 'bg-green-50';
    } else {
      return 'bg-red-50';
    }
  };

  // Remapeia rótulos apenas para exibição no modal (frontend)
  // Observação: não altera o status interno 'taken_late' nem textos na base.
  const getDisplayEventType = (eventType: string) => {
    const t = (eventType || '').trim();
    if (/^Dose perdida(?:\s*(?:\/|ou)\s*muito fora do horário)?\b/i.test(t)) return 'Dose perdida ou muito fora do horário';
    if (t === 'Dose tomada com atraso') return 'Dose tomada fora do horário';
    return t;
  };

  const filteredSum = filteredEvents.reduce((sum, e) => sum + e.points, 0);
  const isFullHistory = filterType === 'all' && filterDate === 'all';
  const balanceSource = isFullHistory && memberTotalPoints !== null ? memberTotalPoints : filteredSum;
  const balanceFormatted = !isFullHistory && balanceSource > 0 ? `+${balanceSource}` : `${balanceSource}`;
  const balanceColor = balanceSource >= 0 ? 'text-green-500' : 'text-red-500';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Histórico de Pontos</h2>
            <p className="text-sm text-gray-600 mt-1">{memberName}</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-2xl text-gray-600"></i>
          </button>
        </div>

        {/* Filtros */}
        <div className="p-6 border-b border-gray-200 space-y-4">
          {/* Filtro por Tipo */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Tipo de Evento</p>
            <div className="flex gap-2">
              <button
                onClick={() => setFilterType('all')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  filterType === 'all'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => setFilterType('positive')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  filterType === 'positive'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Ganhos
              </button>
              <button
                onClick={() => setFilterType('negative')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  filterType === 'negative'
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Perdas
              </button>
              <button
                onClick={() => setFilterType('streak')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  filterType === 'streak'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Streaks
              </button>
            </div>
          </div>

          {/* Filtro por Data */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Período</p>
            <div className="flex gap-2">
              <button
                onClick={() => setFilterDate('all')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  filterDate === 'all'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Tudo
              </button>
              <button
                onClick={() => setFilterDate('today')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  filterDate === 'today'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Hoje
              </button>
              <button
                onClick={() => setFilterDate('week')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  filterDate === 'week'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Semana
              </button>
              <button
                onClick={() => setFilterDate('month')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  filterDate === 'month'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Mês
              </button>
            </div>
          </div>
        </div>

        {/* Lista de Eventos */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando histórico...</p>
              </div>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <i className="ri-file-list-3-line text-6xl text-gray-300 mb-4"></i>
                <p className="text-gray-600 font-medium">Nenhum evento encontrado</p>
                <p className="text-sm text-gray-500 mt-1">Tente ajustar os filtros</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEvents.map((event) => (
                <div
                  key={event.id}
                  className={`flex items-center justify-between p-4 rounded-2xl ${getEventBgColor(event.points, event.eventType)}`}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getEventColor(event.points, event.eventType)} bg-white`}>
                      <i className={`${getEventIcon(event.points, event.eventType)} text-2xl`}></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{getDisplayEventType(event.eventType)}</p>
                      <p className="text-sm text-gray-600">{event.description}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatDate(event.createdAt)}</p>
                    </div>
                  </div>
                  <div className={`text-2xl font-bold ${getEventColor(event.points, event.eventType)}`}>
                    {event.points > 0 ? '+' : ''}{event.points}
                  </div>
                </div>
              ))}
              
              {/* Botão Carregar Mais */}
              {!loading && hasMore && filterType === 'all' && filterDate === 'all' && (
                <div className="flex justify-center pt-4">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-6 py-3 bg-emerald-500 text-white font-medium rounded-2xl hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loadingMore ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Carregando...</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-arrow-down-line text-xl"></i>
                        <span>Carregar mais ({totalCount - events.length} restantes)</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer com Resumo */}
        {!loading && filteredEvents.length > 0 && (
          <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-3xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de eventos {filterType !== 'all' || filterDate !== 'all' ? '(filtrados)' : ''}</p>
                <p className="text-2xl font-bold text-gray-800">{filteredEvents.length}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Saldo do período</p>
                <p className={`text-2xl font-bold ${balanceColor}`}>
                  {balanceFormatted}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
